import { PsHttpService, type PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { coreSearchApiUrlPrefix } from 'library-@/config/constants';
import type { ApiGetTechKeywordRequest, ApiGetTechKeywordResponse, GetTechKeyword } from './interface/get-tech-keyword.interface';
import type { ApiSetTechQueryRequest, ApiSetTechQueryResponse, SetTechQuery} from './interface/set-tech-query.interface';
import type { ApiCheckSensitiveWordRequest, ApiCheckSensitiveWordResponse, CheckSensitiveWord } from './interface/check-sensitive-word.interface';
import type { ApiGetQueryCountRequest, ApiGetQueryCountResponse, GetQueryCount } from './interface/get-query-count.interface';
import type { ApiInitAiLabResponse, InitAiLab } from './interface/init-ai-lab.interface';
import type { ApiGetPatentClmsRequest, ApiGetPatentClmsResponse, GetPatentClms } from './interface/get-patent-clms.interface';
import type { ApiGetMermaidRequest, ApiGetMermaidResponse, GetMermaid } from './interface/get-mermaid.interface';
import type { ApiGetTechAreaRequest, ApiGetTechAreaResponse, GetTechArea } from './interface/get-tech-area.interface';
import type { ApiGetTechSemanticRequest, ApiGetTechSemanticResponse, GetTechSemantic } from './interface/get-tech-semantic.interface';
import type { ApiGetRelatedIpcRequest, ApiGetRelatedIpcResponse, GetRelatedIpc } from './interface/get-related-ipc.interface';
import type { ApiGetClassificationDetailRequest, ApiGetClassificationDetailResponse, GetClassificationDetail } from './interface/get-classification-detail.interface';

export const AiLabApiService = Symbol('AiLabApiService');

export type AiLabApiService = PsHttpService;

@injectable()
export class AiLabApiServiceImpl extends PsHttpService
    implements
    GetTechKeyword,
    SetTechQuery,
    CheckSensitiveWord,
    GetQueryCount,
    InitAiLab,
    GetPatentClms,
    GetMermaid,
    GetTechArea,
    GetTechSemantic,
    GetRelatedIpc,
    GetClassificationDetail
{
    get options() {
        return {
            baseURL: coreSearchApiUrlPrefix,
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }

    async initAiLab() {
        const res = await this.get<PatSnapCommonResponseData<ApiInitAiLabResponse>>({
            url: '/ai-search/init',
            transformCode: true,
            cache: true,
        });
        return res.data;
    }

    async getTechKeyword(data: ApiGetTechKeywordRequest) {
        const res = await this.post<PatSnapCommonResponseData<ApiGetTechKeywordResponse>>({
            url: '/ai-search/keyword',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async checkSensitiveWord(data: ApiCheckSensitiveWordRequest) {
        const res = await this.post<PatSnapCommonResponseData<ApiCheckSensitiveWordResponse>>({
            url: '/ai-search/sensitive-word',
            data,
            cache: true,
            transformCode: true,
        });
        return res.data;
    }

    async getQueryCount(data: ApiGetQueryCountRequest) {
        const res = await this.post<PatSnapCommonResponseData<ApiGetQueryCountResponse>>({
            url: '/patent/query/count',
            data,
            cache: true,
            transformCode: true,
        });
        return res.data;
    }

    async setTechQuery(data: ApiSetTechQueryRequest) {
        const res = await this.post<PatSnapCommonResponseData<ApiSetTechQueryResponse>>({
            url: '/ai-search/tech-query',
            data,
            cache: true,
            transformCode: true,
        });
        return res.data;
    }

    async getPatentClms(data: ApiGetPatentClmsRequest): Promise<ApiGetPatentClmsResponse> {
        const res = await this.get<PatSnapCommonResponseData<ApiGetPatentClmsResponse>>({
            url: '/ai-search/claim-by-query',
            params: data,
            transformCode: true,
        });
        return res.data;
    }

    async getMermaid(data: ApiGetMermaidRequest): Promise<ApiGetMermaidResponse> {
        const res = await this.post<PatSnapCommonResponseData<ApiGetMermaidResponse>>({
            url: '/ai-search/text2mermaid',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getTechArea(data: ApiGetTechAreaRequest): Promise<ApiGetTechAreaResponse> {
        const res = await this.post<PatSnapCommonResponseData<ApiGetTechAreaResponse>>({
            url: '/ai-search/tech-area',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getTechSemantic(data: ApiGetTechSemanticRequest): Promise<ApiGetTechSemanticResponse> {
        const res = await this.post<PatSnapCommonResponseData<ApiGetTechSemanticResponse>>({
            url: '/ai-search/text2semantic',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getRelatedIpc(data: ApiGetRelatedIpcRequest): Promise<ApiGetRelatedIpcResponse> {
        const res = await this.post<PatSnapCommonResponseData<ApiGetRelatedIpcResponse>>({
            url: '/ai-search/text/classification',
            data,
            transformCode: true,
        });

        return res.data;
    }

    getClassificationDetailChannel = Symbol('getClassificationDetailChannel');

    async getClassificationDetail(data: ApiGetClassificationDetailRequest): Promise<ApiGetClassificationDetailResponse> {
        const res = await this.channel<PatSnapCommonResponseData<ApiGetClassificationDetailResponse>>(
            this.getClassificationDetailChannel,
            {
                url: `/classification/detail?type=${data.type}&cid=${data.name}`,
                method: 'GET',
                cache: true,
                transformCode: true,
            },
        );
        return res.data;
    }
}
